import { Box, Typography } from "@mui/material";

export const MAX_FLAVOR_CHARACTERS = 1500;

const isAllowedPlainTextCharacter = (character: string) => {
  const codePoint = character.codePointAt(0) ?? 0;
  return (
    character === "\n" ||
    character === "\t" ||
    (codePoint >= 32 && codePoint !== 127)
  );
};

export const countFlavorCharacters = (value = "") =>
  Array.from(value.replace(/\r\n?/g, "\n")).length;

export const sanitizeFlavorText = (value = "") =>
  Array.from(value.replace(/\r\n?/g, "\n"))
    .filter(isAllowedPlainTextCharacter)
    .slice(0, MAX_FLAVOR_CHARACTERS)
    .join("");

export type FlavorLine = {
  kind: "title" | "paragraph" | "italic" | "bullet" | "space";
  text: string;
};

export const parseFlavorText = (value = ""): FlavorLine[] => {
  const sanitized = sanitizeFlavorText(value);
  if (!sanitized.trim()) return [];

  let titleAssigned = false;
  return sanitized.split("\n").map((rawLine) => {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (!trimmed) return { kind: "space", text: "" };
    if (!titleAssigned) {
      titleAssigned = true;
      return { kind: "title", text: trimmed };
    }
    if (/^_[\s\S]+_$/.test(trimmed)) {
      return { kind: "italic", text: trimmed.slice(1, -1) };
    }
    if (/^-\s+/.test(trimmed)) {
      return { kind: "bullet", text: trimmed.replace(/^-\s+/, "") };
    }
    return { kind: "paragraph", text: line };
  });
};

interface FlavorTextProps {
  text?: string;
  preview?: boolean;
}

const FlavorText = ({ text, preview = false }: FlavorTextProps) => {
  const lines = parseFlavorText(text);
  if (lines.length === 0) return null;

  if (preview) {
    const previewText = lines
      .filter((line) => line.kind !== "space")
      .map((line) => (line.kind === "bullet" ? "• " + line.text : line.text))
      .join(" ");

    return (
      <Typography
        variant="body2"
        component="p"
        aria-label="Matchup story preview"
        sx={{
          m: 0,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          fontStyle: lines[1]?.kind === "italic" ? "italic" : "normal",
          color: "text.secondary",
          lineHeight: "19px",
          minHeight: 38,
        }}
      >
        <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
          {lines[0].text}
        </Box>
        {previewText.startsWith(lines[0].text)
          ? previewText.slice(lines[0].text.length)
          : " " + previewText}
      </Typography>
    );
  }

  return (
    <Box sx={{ width: "100%" }} aria-label="Matchup story">
      {lines.map((line, index) => {
        if (line.kind === "space") {
          return <Box key={"space-" + index} sx={{ height: 12 }} aria-hidden />;
        }

        if (line.kind === "bullet") {
          return (
            <Box
              component="ul"
              key={line.kind + "-" + index}
              sx={{ mt: index === 0 ? 0 : 1, mb: 0, pl: 3 }}
            >
              <Typography
                variant="body2"
                component="li"
                sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
              >
                {line.text}
              </Typography>
            </Box>
          );
        }

        return (
          <Typography
            key={line.kind + "-" + index}
            variant="body2"
            component="p"
            sx={{
              mt: index === 0 ? 0 : 1,
              mb: 0,
              fontWeight: line.kind === "title" ? 700 : 400,
              fontStyle: line.kind === "italic" ? "italic" : "normal",
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {line.text}
          </Typography>
        );
      })}
    </Box>
  );
};

export default FlavorText;
